/**
 * TaseronBaglantilariPage.jsx — Dekoratör ⇄ Taşeron bağlantıları, SAKA (denetmen) tarafı.
 * Route: /Management/Firmalar/TaseronBaglantilari
 *
 * Servis: services/firmaTaseronService.js · services/firmaAramaService.js
 * Rehber: docs/guides/FE-FirmaTaseronController-guide.md
 *
 * Bu ekranın işi TELEFON ETMEK: `durum = 0` satırlar, taşeronu arayıp "gerçekten X'in
 * taşeronu musunuz" diye sormamızı bekleyen bağlantılardır. İki taraf da kendi kararını
 * kendi ekranından verir; buradaki kararlar personel yetkisiyle alınabilen istisnalardır
 * (telefonla halledilen durum).
 *
 * #382 (2026-10-13): paylaşım bayrakları, imzalı formlar ve "Taşeron kadrosu" bağlantısı
 * kalktı — bağlantı yalnızca bir kayıt + iki taraflı onay (bkz. firmaTaseronService.js başlığı).
 *
 * ⚠️ HİÇBİR BİLDİRİM YOK — ne taşerona kapı 1'in beklediği, ne dekoratöre kapı 2'nin.
 * İki taraf da ekranını açınca öğreniyor. Süreci fiilen biz yürütüyoruz; bu yüzden
 * varsayılan filtre "Bekliyor".
 *
 * ⚠️⚠️ KİLİTLENME UYARISI VE SEBEBİ: mükerrer kontrolü yalnızca `aktif = 1`'e bakıyor,
 * `durum`a BAKMIYOR. Yani taşeronun REDDETTİĞİ bir satır (durum 2, aktif 1) aynı üçlü
 * için her yeni bağlantıyı **409** ile engelliyor. Çıkış tek: bağlantıyı sonlandırmak.
 * Bu yüzden reddedilmiş satırların yanındaki "Sonlandır" butonu kozmetik değil, kuyruğun
 * kilidini açan şey.
 *
 * ⚠️ `fuarId` GÖNDERİLMEZSE backend tüm fuarların bağlantılarını döner — servis bunu
 * zorunlu tutuyor. Sayfalama yok; liste istemci tarafında filtreleniyor.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card, CardBody, CardHeader, Row, Col, Button, Badge, Spinner, Alert, Input, Label,
  Table, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Nav, NavItem, NavLink,
} from "reactstrap";
import {
  Handshake, RefreshCw, Plus, AlertTriangle, Info, Phone,
} from "lucide-react";
import { PageFrame, PageBody } from "../../../../Components/Common/EmbeddablePage";
import MainContext from "../../../../Components/Contexts/MainContext";
import FirmaSearchSelect from "../../../../Components/Firma/FirmaSearchSelect";
import {
  getFuarTaseronBaglantilari, createTaseronBaglantisi, endTaseronBaglantisi,
  taseronKarar, dekoratorKarar, geriAlTaseronKarar, geriAlDekoratorKarar,
  TASERON_DURUM, DEKORATOR_ONAY, getTaseronTipleri, KAPI, KAPI_ADI,
} from "../../../../services/firmaTaseronService";
import { GeriAlModal } from "../../../../Components/Firma/TaseronKararParcalari";
import { DEFAULT_FUAR_ID } from "../../../../services/personnelAdminService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../../i18n";

const DURUM_FILTRELERI = [
  { key: 0, get label() { return i18n.t("moduller:taseronBaglantilari.bekliyor"); } },
  { key: 1, get label() { return i18n.t("moduller:taseronBaglantilari.onaylandi"); } },
  { key: 2, get label() { return i18n.t("moduller:taseronBaglantilari.reddedildi"); } },
  { key: null, get label() { return i18n.t("moduller:taseronBaglantilari.hepsi"); } },
];

function formatTarih(value) {
  if (!value) return "—";
  const d = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString(currentLocaleTag());
}

/**
 * @param {boolean} embedded  true → sayfa kabuğu ve fuar geçiş kutusu basılmaz; liste
 *                            `firmaId`'ye daraltılır (firma ister ana firma ister taşeron
 *                            tarafında olsun). FirmaDetayPage.jsx'in "Taşeron Bağlantıları"
 *                            sekmesi bu modda kullanır. Bağımsız route fuar geneli kuyruk
 *                            olarak kalmaya devam ediyor.
 */
export default function TaseronBaglantilariPage({ embedded = false, firmaId: firmaIdProp, fuarId: fuarIdProp }) {
  const { t: tx } = useTranslation("moduller");
  const { selectedFuarId } = useContext(MainContext);
  const [fuarIdInput, setFuarIdInput] = useState(String(fuarIdProp || Number(selectedFuarId) || DEFAULT_FUAR_ID));
  const fuarId = fuarIdProp || Number(fuarIdInput) || DEFAULT_FUAR_ID;

  const [durum, setDurum] = useState(embedded ? null : 0);
  const [sadeceAktif, setSadeceAktif] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const [createModal, setCreateModal] = useState(false);
  const [sonlandirModal, setSonlandirModal] = useState(null);
  // { row, kapi } — personel, yanlış verilmiş bir cevabı iki taraf adına da geri alabilir.
  const [geriAlModal, setGeriAlModal] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => { document.title = i18n.t("moduller:taseronBaglantilari.taseronBaglantilariFds"); }, []);

  // Yeniden okuma sayaçla tetikleniyor: effect gövdesinde doğrudan setState çağırmak
  // react-hooks/set-state-in-effect'i ihlal ediyor (iş iç fonksiyona alınınca etmiyor).
  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const list = await getFuarTaseronBaglantilari({ fuarId, durum, sadeceAktif });
        if (!ignore) setRows(list);
      } catch (e) {
        if (!ignore) { setError(e.message); setRows([]); }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [fuarId, durum, sadeceAktif, reloadToken]);

  async function run(fn, successText) {
    try {
      await fn();
      setFlash({ color: "success", text: successText });
      setSonlandirModal(null);
      setGeriAlModal(null);
      setCreateModal(false);
      reload();
    } catch (e) {
      setFlash({ color: "danger", text: e.message });
    }
  }

  // Gömülü modda yalnızca bu firmanın taraf olduğu satırlar — uç fuar geneli döner,
  // firma filtresi yok, o yüzden istemci tarafında daraltılıyor (liste zaten sayfasız).
  const gorunenRows = useMemo(
    () => (embedded && firmaIdProp
      ? rows.filter((r) => Number(r.anaFirmaId) === Number(firmaIdProp) || Number(r.taseronFirmaId) === Number(firmaIdProp))
      : rows),
    [rows, embedded, firmaIdProp]
  );

  const bekleyenSayisi = useMemo(() => gorunenRows.filter((r) => r.durum === 0).length, [gorunenRows]);

  return (
    <PageFrame embedded={embedded}>
      <PageBody embedded={embedded} title={tx("taseronBaglantilari.taseronBaglantilari")} pageTitle={tx("taseronBaglantilari.firmalar")}>

        {flash && <Alert fade={false} color={flash.color} toggle={() => setFlash(null)}>{flash.text}</Alert>}

        <Card className="mb-3">
          <CardBody>
            <Row className="g-2 align-items-end">
              {!embedded && (
              <Col md={2}>
                <Label className="small">{tx("taseronBaglantilari.fuarId")}</Label>
                <Input value={fuarIdInput} onChange={(e) => setFuarIdInput(e.target.value)} />
              </Col>
              )}
              <Col md>
                <Nav pills>
                  {DURUM_FILTRELERI.map((f) => (
                    <NavItem key={String(f.key)}>
                      <NavLink
                        href="#" className={durum === f.key ? "active" : ""}
                        onClick={(e) => { e.preventDefault(); setDurum(f.key); }}
                      >
                        {f.label}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </Col>
              <Col md="auto" className="d-flex align-items-center gap-3">
                <FormGroup switch className="mb-0">
                  <Input
                    type="switch" id="aktif" checked={!sadeceAktif}
                    onChange={(e) => setSadeceAktif(!e.target.checked)}
                  />
                  <Label check for="aktif" className="small mb-0">{tx("taseronBaglantilari.sonlandirilanlarDahil")}</Label>
                </FormGroup>
                <Button size="sm" color="light" onClick={reload} disabled={loading}>
                  <RefreshCw size={14} />
                </Button>
                <Button size="sm" color="success" onClick={() => setCreateModal(true)}>
                  <Plus size={14} className="me-1" />{tx("taseronBaglantilari.baglantiAc")}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {bekleyenSayisi > 0 && (
          <Alert fade={false} color="warning" className="d-flex gap-2">
            <Phone size={18} className="flex-shrink-0 mt-1" />
            <div className="small">
              <strong>{bekleyenSayisi}{" "}{tx("taseronBaglantilari.baglantiTaseronunCevabiniBekliyor")}</strong>{" "}{tx("taseronBaglantilari.sistemdeBildirimYokTaseronKendi")}
            </div>
          </Alert>
        )}

        <Card>
          <CardHeader className="bg-transparent d-flex align-items-center gap-2">
            <Handshake size={16} className="text-muted" />
            <span className="fw-semibold">
              {embedded ? tx("taseronBaglantilari.buFirmaBaglanti", { length: gorunenRows.length }) : tx("taseronBaglantilari.fuarBaglanti", { fuarId, length: gorunenRows.length })}
            </span>
          </CardHeader>
          <CardBody>
            {error && <Alert fade={false} color="danger">{error}</Alert>}

            {loading ? (
              <div className="text-center py-5"><Spinner /></div>
            ) : gorunenRows.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <Handshake size={32} className="mb-2" />
                <div>{tx("taseronBaglantilari.buFiltreyleBaglantiYok")}</div>
                <div className="small mt-1" style={{ maxWidth: 640, margin: "0 auto" }}>
                  {tx("taseronBaglantilari.baglantilarIkiYoldanDogarBir")}
                  <strong>{tx("taseronBaglantilari.taseronAtamaTalebini")}</strong>{" "}{tx("taseronBaglantilari.onayladiginizdaYaDaBuradanDogrudan")}
                  <Link to="/Management/Projects">{tx("taseronBaglantilari.standListesi")}</Link>{" "}{tx("taseronBaglantilari.standDuzenleFirmaAtamaTalepleri")}
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{tx("taseronBaglantilari.dekoratorAnaFirma")}</th>
                      <th>{tx("taseronBaglantilari.taseron")}</th>
                      <th>{KAPI_ADI[KAPI.TASERON_BEYANI]}</th>
                      <th>{KAPI_ADI[KAPI.DEKORATOR_ONAYI]}</th>
                      <th className="text-end">{tx("taseronBaglantilari.islem")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gorunenRows.map((r) => {
                      const d = TASERON_DURUM[r.durum] ?? TASERON_DURUM[0];
                      const o = DEKORATOR_ONAY[r.dekoratorOnay] ?? DEKORATOR_ONAY[0];
                      return (
                        <tr key={r.id} className={r.aktif ? "" : "opacity-50"}>
                          <td>
                            <div className="fw-medium">{r.anaFirmaAdi}</div>
                            <div className="small text-muted">{tx("taseronBaglantilari.id")}{" "}{r.anaFirmaId}</div>
                            <div className="small text-muted">
                              {r.talepId ? tx("taseronBaglantilari.talep", { talepId: r.talepId }) : tx("taseronBaglantilari.dogrudanAcildiTalepYok")}
                            </div>
                          </td>
                          <td>
                            <div className="fw-medium">
                              {r.taseronFirmaAdi}
                              {!r.aktif && <Badge color="secondary" className="ms-2 fw-normal">{tx("taseronBaglantilari.pasif")}</Badge>}
                            </div>
                            <div className="small text-muted">
                              {tx("taseronBaglantilari.id2", { taseronFirmaId: r.taseronFirmaId })}
                              {r.taseronFirmaTypeAdi ? ` · ${r.taseronFirmaTypeAdi}` : ""}
                            </div>
                            <div className="small text-muted">{formatTarih(r.talepTarihi)}</div>
                          </td>
                          <td>
                            <Badge color={d.color} className="fw-normal">{r.durumAdi || d.label}</Badge>
                            {r.redSebebi && (
                              <div className="small text-danger mt-1" style={{ whiteSpace: "normal", maxWidth: 240 }}>
                                {r.redSebebi}
                              </div>
                            )}
                          </td>
                          <td>
                            <Badge color={o.color} className="fw-normal">{r.dekoratorOnayAdi || o.label}</Badge>
                            {r.dekoratorRedSebebi && (
                              <div className="small text-danger mt-1" style={{ whiteSpace: "normal", maxWidth: 240 }}>
                                {r.dekoratorRedSebebi}
                              </div>
                            )}
                          </td>
                          <td className="text-end">
                            {/* Personel iki kapıyı da telefonla alınan cevapla kapatabilir. */}
                            {r.aktif && r.durum === 0 && (
                              <Button
                                size="sm" color="success" className="me-1 mb-1"
                                onClick={() => run(
                                  () => taseronKarar(r.id, { onayla: true }),
                                  tx("taseronBaglantilari.taseronAdinaOnaylandi"),
                                )}
                                title={tx("taseronBaglantilari.telefondaAlinanOnay")}
                              >
                                {tx("taseronBaglantilari.beyaniOnayla")}
                              </Button>
                            )}
                            {r.aktif && r.dekoratorOnay === 0 && (
                              <Button
                                size="sm" color="primary" outline className="me-1 mb-1"
                                onClick={() => run(
                                  () => dekoratorKarar(r.id, { onayla: true }),
                                  tx("taseronBaglantilari.dekoratorAdinaOnaylandi"),
                                )}
                              >
                                {tx("taseronBaglantilari.onayiVer")}
                              </Button>
                            )}
                            {/* Yanlış kaydedilmiş bir cevap artık satırı kilitlemiyor: geri
                                alınır, eskisi tarihçeye yazılır (#67). */}
                            {r.aktif && (r.durum === 1 || r.durum === 2) && (
                              <Button
                                size="sm" color="light" className="me-1 mb-1"
                                onClick={() => setGeriAlModal({ row: r, kapi: KAPI.TASERON_BEYANI })}
                              >
                                {tx("taseronBaglantilari.beyaniGeriAl")}
                              </Button>
                            )}
                            {r.aktif && (r.dekoratorOnay === 1 || r.dekoratorOnay === 2) && (
                              <Button
                                size="sm" color="light" className="me-1 mb-1"
                                onClick={() => setGeriAlModal({ row: r, kapi: KAPI.DEKORATOR_ONAYI })}
                              >
                                {tx("taseronBaglantilari.onayiGeriAl")}
                              </Button>
                            )}
                            {r.aktif && (
                              <Button size="sm" color="danger" outline className="mb-1" onClick={() => setSonlandirModal(r)}>
                                {tx("taseronBaglantilari.sonlandir")}
                              </Button>
                            )}
                            {r.aktif && r.durum === 2 && (
                              <div className="small text-warning mt-1" style={{ whiteSpace: "normal", maxWidth: 220 }}>
                                {tx("taseronBaglantilari.reddedilmisAmaHalaAktifAyni")}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        <CreateBaglantiModal
          key={createModal ? "create-acik" : "create-kapali"}
          isOpen={createModal}
          fuarId={fuarId}
          onClose={() => setCreateModal(false)}
          onSubmit={(payload) => run(() => createTaseronBaglantisi(payload), tx("taseronBaglantilari.baglantiAcildiIkiKapiDa"))}
        />

        <GeriAlModal
          key={`geri-${geriAlModal?.row?.id ?? "yok"}-${geriAlModal?.kapi ?? 0}`}
          row={geriAlModal?.row}
          kapi={geriAlModal?.kapi}
          personelAdina
          onClose={() => setGeriAlModal(null)}
          onSubmit={(payload) => run(
            () => (geriAlModal.kapi === KAPI.TASERON_BEYANI
              ? geriAlTaseronKarar(geriAlModal.row.id, payload)
              : geriAlDekoratorKarar(geriAlModal.row.id, payload)),
            tx("taseronBaglantilari.geriAlindi", { v: KAPI_ADI[geriAlModal.kapi] }),
          )}
        />

        <Modal isOpen={!!sonlandirModal} toggle={() => setSonlandirModal(null)}>
          <ModalHeader toggle={() => setSonlandirModal(null)}>{tx("taseronBaglantilari.baglantiyiSonlandir")}</ModalHeader>
          <ModalBody>
            {sonlandirModal && (
              <>
                <p>
                  <strong>{sonlandirModal.anaFirmaAdi}</strong> ⇄{" "}
                  <strong>{sonlandirModal.taseronFirmaAdi}</strong>{" "}{tx("taseronBaglantilari.baglantisiPasifeAlinacak")}
                </p>
                <Alert fade={false} color="secondary" className="small mb-0">
                  {tx("taseronBaglantilari.satirSilinmezOnayliysaIptalDurumuna2")}
                  {sonlandirModal.durum === 2 && (
                    <div className="mt-1 text-warning">
                      {tx("taseronBaglantilari.buSatirReddedilmisSonlandirmadanAyni")}
                    </div>
                  )}
                </Alert>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setSonlandirModal(null)}>{tx("taseronBaglantilari.vazgec")}</Button>
            <Button color="danger" onClick={() => run(() => endTaseronBaglantisi(sonlandirModal.id), tx("taseronBaglantilari.baglantiSonlandirildi"))}>
              {tx("taseronBaglantilari.sonlandir")}
            </Button>
          </ModalFooter>
        </Modal>
      </PageBody>
    </PageFrame>
  );
}

/**
 * Personelin doğrudan bağlantı açması. Talep-ve-onay yolunu atlar, ama ONAYLARI ATLAMAZ:
 * satır iki kapı da kapalı doğar.
 * ⚠️ `taseronFirmaTypeId` gönderilirse >= 4 olmak ZORUNDA — Dekoratör (3) buraya ait
 * değil, o hâlâ `firma_to_stand`'e yazılır.
 */
function CreateBaglantiModal({ isOpen, fuarId, onClose, onSubmit }) {
  const { t: tx } = useTranslation("moduller");
  const [ana, setAna] = useState(null);
  const [taseron, setTaseron] = useState(null);
  const [typeId, setTypeId] = useState("");
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Sıfırlama effect'i YOK — modal `key` ile yeniden mount ediliyor, state her açılışta
  // baştan doğuyor. Effect yalnızca dış sistemi (firma tipleri) okuyor.
  useEffect(() => {
    let ignore = false;
    getTaseronTipleri()
      .then((list) => { if (!ignore) setTypes(list); })
      .catch(() => { if (!ignore) setTypes([]); });
    return () => { ignore = true; };
  }, []);

  if (!isOpen) return null;

  async function submit() {
    if (!ana?.firmaId || !taseron?.firmaId) { setError(tx("taseronBaglantilari.herIkiFirmayiDaSecin")); return; }
    if (ana.firmaId === taseron.firmaId) { setError(tx("taseronBaglantilari.birFirmaKendiTaseronuOlarak")); return; }
    setBusy(true); setError(null);
    try {
      await onSubmit({
        fuarId,
        anaFirmaId: ana.firmaId,
        taseronFirmaId: taseron.firmaId,
        taseronFirmaTypeId: Number(typeId) || undefined,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen toggle={() => !busy && onClose()} size="lg">
      <ModalHeader toggle={() => !busy && onClose()}>{tx("taseronBaglantilari.taseronBaglantisiAcFuar")}{" "}{fuarId}</ModalHeader>
      <ModalBody>
        {error && <Alert fade={false} color="danger">{error}</Alert>}

        <Alert fade={false} color="info" className="small d-flex gap-2">
          <Info size={18} className="flex-shrink-0 mt-1" />
          <div>
            {tx("taseronBaglantilari.buYolTalepEvrakiniAtlar")}{" "}<strong>{tx("taseronBaglantilari.onaylariAtlamaz")}</strong>{tx("taseronBaglantilari.baglantiIkiKapiDaKapali")}
          </div>
        </Alert>

        <Row className="g-3">
          <Col md={6}>
            <FirmaSearchSelect
              label={tx("taseronBaglantilari.dekoratorAnaFirma2")} value={ana} onChange={setAna} forceLive
              hint={tx("taseronBaglantilari.taseronuTutanFirma")}
            />
          </Col>
          <Col md={6}>
            <FirmaSearchSelect
              label={tx("taseronBaglantilari.taseronFirma")} value={taseron} onChange={setTaseron} forceLive
              hint={tx("taseronBaglantilari.isiYapanFirmaZeminTemizlik")}
            />
          </Col>
          <Col md={6}>
            <Label className="small">{tx("taseronBaglantilari.taseronTipi")}</Label>
            <Input type="select" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
              <option value="">{tx("taseronBaglantilari.belirtilmedi4TaseronVarsayilir")}</option>
              {types.map((t) => (
                <option key={t.firmaTypeId} value={String(t.firmaTypeId)}>
                  {/* `label` servis tarafında zaten `firmaTypeName → firmaTypeValue → Tip N`
                      sırasıyla çözülüyor — 14 Güvenlik'in adı null olduğu için bu şart. */}
                  {t.label}
                </option>
              ))}
            </Input>
            <div className="form-text small">
              {tx("taseronBaglantilari.yalnizcaEtiketHicbirKuralBu")}
            </div>
          </Col>
        </Row>

        <Alert fade={false} color="warning" className="small mt-3 mb-0 d-flex gap-2">
          <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
          <div>
            {tx("taseronBaglantilari.ayniIkiliIcin")}{" "}<strong>{tx("taseronBaglantilari.aktif")}</strong>{" "}{tx("taseronBaglantilari.birBaglantiVarsaReddedilmisOlsa")}
          </div>
        </Alert>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={busy}>{tx("taseronBaglantilari.vazgec")}</Button>
        <Button color="success" onClick={submit} disabled={busy}>
          {busy ? <Spinner size="sm" /> : tx("taseronBaglantilari.baglantiyiAc")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
